import { apiClient } from 'lib/api';
import { useMutation } from '@tanstack/react-query';
import type { MutationConfig } from 'lib/react-query';
import { isAxiosError } from 'axios';
import type { AdSetEntry } from './getAdSets';
import type { CreativePayloadEntry } from './types';

export interface CreateScalingEgAdsParams {
  app?: string;
  platform?: string;
  ad_type: string;
  creatives: CreativePayloadEntry[];
  ad_sets: AdSetEntry[] | null;
  call_to_action?: string;
  hotjar?: boolean;
  conversion_domain?: string;
  ad_settings?: Record<string, unknown>;
  optional_ad_label?: string;
}

export interface CreateScalingEgAdsResponse {
  job_id: string;
}

const createScalingEgAds = async (
  body: CreateScalingEgAdsParams,
): Promise<CreateScalingEgAdsResponse> => {
  const res = await apiClient.post<CreateScalingEgAdsResponse>(
    '/api/v1/facebook_manager/create_ad',
    body,
  );

  if (isAxiosError(res)) {
    const error = new Error('Create Scaling EG Ads Error');
    error.cause = {
      ...res.response?.data,
      status: res.response?.status,
    };
    return Promise.reject(error);
  }
  return res.data;
};

const createScalingEgAdsLocal = (
  body: CreateScalingEgAdsParams,
): Promise<CreateScalingEgAdsResponse> => {
  console.log('[DEV] create_ads payload:', body);
  return new Promise((resolve) => {
    resolve({ job_id: 'mock-scaling-eg-job-id' });
  });
};

const submitScalingEgAds =
  process.env.NODE_ENV === 'development'
    ? createScalingEgAdsLocal
    : createScalingEgAds;

interface UseCreateScalingEgAdsOptions {
  config?: MutationConfig<typeof submitScalingEgAds>;
}

const useCreateScalingEgAds = ({ config }: UseCreateScalingEgAdsOptions = {}) => {
  return useMutation({
    ...config,
    onSuccess: (...args) => {
      if (config?.onSuccess) {
        config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: submitScalingEgAds,
  });
};

export { createScalingEgAds, useCreateScalingEgAds };
